/**
 * Sentry error logging configuration
 * Captures and reports application errors for monitoring (FR38)
 *
 * @module lib/sentry
 */

import * as Sentry from '@sentry/react';

/**
 * Benign Firebase IndexedDB teardown error, dropped globally (all browsers).
 *
 * The Firebase SDK (Auth/Installations session storage, and Firestore when
 * persistence is enabled) keeps a handle to IndexedDB. iOS — especially inside
 * Meta's in-app browser — reclaims that connection when the WebView is
 * backgrounded or torn down, and a pending async call rejects with
 * "UnknownError: Connection to Indexed Database server lost. Refresh the page to
 * try again". It surfaces as an unhandled promise rejection, so the user sees
 * nothing (no crash, no UI break); the SDK works in memory regardless.
 *
 * Unlike the Meta-bridge noise this is NOT gated on in-app-browser detection:
 * it is benign in every browser (Safari private mode, multi-tab, storage
 * pressure all trigger it) and never represents a Sagamoon bug, so a global
 * `ignoreErrors` rule is safe.
 *
 * @see docs/design/originals/Tests/sentry error unknown.md (issue 85256292)
 */
export const INDEXED_DB_CONNECTION_LOST = /Connection to Indexed Database server lost/;

/**
 * Benign Firebase IndexedDB teardown error, dropped globally (all browsers).
 *
 * Same root cause as {@link INDEXED_DB_CONNECTION_LOST} but a different message:
 * when iOS (especially Meta's in-app browser) partitions or reclaims the
 * WebView's IndexedDB, an in-flight Firebase transaction finds its object store
 * gone and rejects with "InvalidStateError: Object store cannot be found in the
 * database" (DOMException.code 11). It surfaces as an unhandled promise
 * rejection, so the user sees nothing — no crash, no UI break; the SDK works in
 * memory regardless. Kept as a separate, precisely-named rule (not folded into
 * the connection-lost regex) so each captured payload is documented and tested.
 *
 * Like the connection-lost case this is benign in every browser, so a global
 * `ignoreErrors` rule is safe (not gated on in-app-browser detection).
 *
 * @see docs/design/originals/Tests/sentry error object-store-not-found.md (issue 130626412)
 */
export const INDEXED_DB_OBJECT_STORE_MISSING = /Object store cannot be found in the database/;

/**
 * Benign WebView-teardown abort, dropped globally (all browsers).
 *
 * When Meta's iOS in-app browser backgrounds or closes the WebView, iOS aborts
 * any in-flight `fetch` — most commonly the Firebase Firestore `Listen/channel`
 * long-poll that stays open for realtime updates. The request rejects with
 * "AbortError: The operation was aborted" (`DOMException.code = 20`) and, because
 * nothing owns the promise at teardown, it surfaces as an unhandled promise
 * rejection that Sentry's global handler auto-captures. The user sees nothing —
 * the tab is being closed.
 *
 * This is never a Sagamoon bug: a repo-wide search finds zero `AbortController` /
 * `.abort()` usage, so we never abort anything intentionally. Any `AbortError` is
 * therefore browser/SDK-initiated teardown, benign in every browser, so a global
 * `ignoreErrors` rule is safe (not gated on in-app-browser detection) — same
 * treatment as {@link INDEXED_DB_CONNECTION_LOST} / {@link INDEXED_DB_OBJECT_STORE_MISSING}.
 *
 * @see docs/design/originals/Tests/sentry error operation-aborted.md (issue 131514223)
 */
export const OPERATION_ABORTED = /AbortError: The operation was aborted/;

/**
 * True when a Sentry event is Meta in-app-browser (Facebook/Instagram WebView)
 * bridge noise rather than Sagamoon application code.
 *
 * FB/Instagram ad clicks open the landing page inside Meta's in-app browser,
 * which injects its own instrumentation (navigation performance logger, native
 * bridge). During WebView teardown those scripts call a native bridge that is
 * already gone and throw — Sentry auto-wraps addEventListener/onerror and
 * reports them as if they were ours. They are not: a repo-wide search found zero
 * source references to any of these bridge APIs (window.webkit.messageHandlers,
 * sendDataToNative, sendPageShowMessage, hxp-chat-suppression, etc.).
 *
 * Pure and event-driven so it can be unit-tested against captured payloads.
 * **Gated on Meta-IAB detection** so a genuine Safari/WebKit error in a normal
 * browser is never suppressed.
 *
 * @param event - the Sentry event under consideration
 * @param userAgent - optional UA override (defaults to navigator UA when present)
 * @returns true if the event should be dropped as Meta IAB noise
 */
export function isMetaIabBridgeNoise(event: Sentry.Event, userAgent?: string): boolean {
  const exception = event.exception?.values?.[0];
  const message = exception?.value ?? '';
  const stack =
    exception?.stacktrace?.frames
      ?.map((frame) => `${frame.filename ?? ''} ${frame.function ?? ''}`)
      .join(' ') ?? '';

  const ua = userAgent ?? (typeof navigator !== 'undefined' ? navigator.userAgent : '');
  const browserName = String(event.contexts?.browser?.name ?? event.tags?.browser ?? '');

  const isMetaIab = /Instagram|Facebook/i.test(browserName) || /Instagram|FBAN|FBAV/i.test(ua);
  if (!isMetaIab) return false;

  return (
    /Java object is gone/i.test(message) ||
    /Error invoking postMessage/i.test(message) ||
    /Error invoking enableButtonsClickedMetaDataLogging/i.test(message) ||
    (/window\.webkit\.messageHandlers/i.test(message) &&
      (/sendDataToNative|sendPageShowMessage|IAB unified bridge|hxp-chat-suppression/i.test(
        stack
      ) ||
        isMetaIab)) ||
    /iabjs:\/\//i.test(stack) ||
    /navigation_performance_logger_android/i.test(stack)
  );
}

/**
 * Initialize Sentry error logging
 * Should be called as early as possible in app initialization
 *
 * @param dsn - Sentry DSN from environment variable
 *
 * @example
 * ```typescript
 * // In main.tsx
 * import { initSentry } from './lib/sentry';
 * initSentry(import.meta.env.VITE_SENTRY_DSN);
 * ```
 */
export function initSentry(dsn?: string): void {
  // Only initialize Sentry in production or if DSN is explicitly provided
  if (!dsn) {
    if (import.meta.env.PROD) {
      console.warn('⚠️ Sentry DSN not configured. Error logging is disabled.');
    } else {
      console.info('ℹ️ Sentry not configured for development (set VITE_SENTRY_DSN to enable)');
    }
    return;
  }

  Sentry.init({
    dsn,
    // Set environment (production, staging, development)
    environment: import.meta.env.MODE,

    // Only send errors in production to reduce noise
    enabled: import.meta.env.PROD,

    // Performance monitoring - sample 10% of transactions in production
    tracesSampleRate: import.meta.env.PROD ? 0.1 : 1.0,

    // Capture unhandled promise rejections
    integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],

    // Session Replay sampling rates
    replaysSessionSampleRate: 0.1, // 10% of sessions
    replaysOnErrorSampleRate: 1.0, // 100% of sessions with errors

    // Ignore third-party noise we cannot fix. Facebook/Instagram in-app
    // browsers (Android WebViews) inject their own instrumentation script
    // ("iabjs://navigation_performance_logger_android"). During page teardown
    // it calls a native Java bridge that has already been GC'd, throwing
    // "... : Java object is gone". Sentry auto-wraps addEventListener, so it
    // reports these as if they were ours. They do not affect our users.
    ignoreErrors: [
      /Java object is gone/,
      INDEXED_DB_CONNECTION_LOST,
      INDEXED_DB_OBJECT_STORE_MISSING,
      OPERATION_ABORTED,
    ],

    // Drop any event whose stack originates from injected in-app-browser scripts.
    denyUrls: [/iabjs:\/\//, /iabmcjs:\/\//],

    // Filter out known non-critical errors
    beforeSend(event, hint) {
      // Don't send development errors
      if (!import.meta.env.PROD) {
        return null;
      }

      const error = hint.originalException;

      // Filter out Firebase "already connected to emulator" errors
      if (error instanceof Error && error.message.includes('already been called')) {
        return null;
      }

      // Drop Meta in-app-browser bridge noise (FB/Instagram WebView). This also
      // catches the Instagram-iOS `window.webkit.messageHandlers` case that the
      // `denyUrls: [/iabjs/]` rule can't, because iOS injects the script into our
      // page context (frame URL = sagamoon.com). See isMetaIabBridgeNoise.
      if (isMetaIabBridgeNoise(event)) {
        return null;
      }

      return event;
    },
  });

  // Tag Meta in-app-browser sessions (FB/IG ad clicks open inside a WebView) so
  // we can gauge IAB share of traffic and separate IAB noise from real errors.
  if (typeof navigator !== 'undefined') {
    Sentry.setTag(
      'in_app_browser',
      /Instagram|FBAN|FBAV/i.test(navigator.userAgent) ? 'meta' : 'no'
    );
  }

  console.info('✅ Sentry error logging initialized');
}

/**
 * Log an error to Sentry with additional context
 * Use this instead of console.error for production error logging
 *
 * @param error - Error object or message
 * @param context - Additional context to help debug the error
 *
 * @example
 * ```typescript
 * import { logError } from '@/lib/sentry';
 *
 * try {
 *   await signUp(email, password);
 * } catch (error) {
 *   logError(error, {
 *     email,
 *     action: 'signup',
 *     timestamp: new Date().toISOString(),
 *   });
 * }
 * ```
 */
export function logError(error: unknown, context?: Record<string, unknown>): void {
  // Always log to console in development
  if (import.meta.env.DEV) {
    console.error('Error:', error);
    if (context) {
      console.error('Context:', context);
    }
  }

  // Capture to Sentry with context
  Sentry.withScope((scope) => {
    if (context) {
      scope.setContext('additional', context);
    }

    if (error instanceof Error) {
      Sentry.captureException(error);
    } else {
      Sentry.captureMessage(String(error), 'error');
    }
  });
}

/**
 * Record a benign, expected condition as an info-level Sentry breadcrumb (NOT an
 * error). Use for states that are normal-but-notable — e.g. a first-time social
 * user with no household yet (→ onboarding), or a transient Firestore offline
 * blip the SDK auto-recovers from — so they add context to any later real error
 * without themselves counting toward error-rate alerts. [Sentry 131603075/131606349]
 */
export function logBreadcrumb(message: string, context?: Record<string, unknown>): void {
  if (import.meta.env.DEV) {
    console.info('Breadcrumb:', message, context ?? '');
  }

  Sentry.addBreadcrumb({
    level: 'info',
    category: 'app',
    message,
    data: context,
  });
}

// Re-export Sentry for advanced usage
export { Sentry };
